import type { TranscriptWord } from "./types";

/** One on-screen caption: up to MAX_BLOCK_WORDS words, or a sentence, whichever ends first. */
export interface CaptionBlock {
  text: string;
  startTime: number;
  endTime: number;
}

const MAX_BLOCK_WORDS = 10;
/** A caption stays up this long after its last word when nothing follows straight away. */
const HOLD_SECONDS = 1.5;
/** Used to place words in time when a transcript has no usable timings and the audio's length is unknown. */
const WORDS_PER_SECOND = 2.5;

/**
 * A WebVTT timestamp in seconds. Workers AI Whisper writes short audio as "SS.mmm" (no minutes),
 * longer audio as "MM:SS.mmm", and other tools write "HH:MM:SS.mmm" (or a comma before the
 * milliseconds). NaN when it can't be read.
 */
export function parseVttTime(raw: string): number {
  const s = raw.trim().split(/\s+/)[0]?.replace(",", ".") ?? "";
  if (!/^\d+(:\d+){0,2}(\.\d+)?$/.test(s)) return NaN;
  return s.split(":").reduce((total, part) => total * 60 + parseFloat(part), 0);
}

/** The cues of a WebVTT document, each with its words spread evenly over the cue's time. */
export function wordsFromVtt(vtt: string): TranscriptWord[] {
  const words: TranscriptWord[] = [];
  let cue: { start: number; end: number; text: string[] } | null = null;
  const flush = () => {
    if (!cue) return;
    const parts = cue.text.join(" ").split(/\s+/).filter(Boolean);
    const per = parts.length ? Math.max(0, cue.end - cue.start) / parts.length : 0;
    parts.forEach((word, i) => words.push({ word, start: cue!.start + i * per, end: cue!.start + (i + 1) * per }));
    cue = null;
  };
  for (const rawLine of vtt.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line.includes("-->")) {
      flush();
      const [from, to] = line.split("-->");
      const start = parseVttTime(from);
      const end = parseVttTime(to);
      cue = Number.isFinite(start) && Number.isFinite(end) ? { start, end, text: [] } : null;
    } else if (!line) {
      flush();
    } else if (cue) {
      cue.text.push(line);
    }
  }
  flush();
  return words;
}

/**
 * The words of a Workers AI Whisper response. Its own `words` array carries per-word timings and
 * is used when it has any; the `vtt` track is the fallback.
 */
export function whisperWords(response: { words?: unknown; vtt?: unknown }): TranscriptWord[] {
  const native = Array.isArray(response.words) ? cleanWords(response.words) : [];
  if (native.length && timed(native)) return native;
  const fromVtt = typeof response.vtt === "string" ? wordsFromVtt(response.vtt) : [];
  if (fromVtt.length && timed(fromVtt)) return fromVtt;
  return native.length ? native : fromVtt;
}

/** Words with a non-empty `word` and numeric times (stored transcripts may hold strings or nulls). */
function cleanWords(words: unknown[]): TranscriptWord[] {
  const out: TranscriptWord[] = [];
  for (const w of words as { word?: unknown; start?: unknown; end?: unknown }[]) {
    const word = typeof w?.word === "string" ? w.word.trim() : "";
    if (!word) continue;
    const start = Number(w.start);
    const end = Number(w.end);
    out.push({ word, start: Number.isFinite(start) ? start : 0, end: Number.isFinite(end) ? end : 0 });
  }
  return out;
}

/** False when every word sits at the same instant (a transcript whose timestamps were lost). */
export function hasTimings(words: readonly unknown[]): boolean {
  return timed(cleanWords([...words]));
}

function timed(words: TranscriptWord[]): boolean {
  return words.some((w) => w.end > words[0].start || w.start !== words[0].start);
}

/**
 * Words with times the caption engine can use:
 * - a run of words sharing one start and end (a segment-level dump) is spread evenly over it, and a
 *   run with no length is spread up to where the next run starts;
 * - a transcript with no usable timings at all is spread over `audioSeconds` (or, when that isn't
 *   known, at an ordinary speaking pace), so its captions still go by in order.
 */
export function normalizeWords(words: readonly unknown[], audioSeconds?: number): TranscriptWord[] {
  const clean = cleanWords([...words]);
  if (!clean.length) return [];
  if (!timed(clean)) {
    const total = audioSeconds && Number.isFinite(audioSeconds) && audioSeconds > 0 ? audioSeconds : clean.length / WORDS_PER_SECOND;
    const per = total / clean.length;
    return clean.map((w, i) => ({ word: w.word, start: i * per, end: (i + 1) * per }));
  }
  const out: TranscriptWord[] = [];
  let i = 0;
  while (i < clean.length) {
    let j = i + 1;
    while (j < clean.length && clean[j].start === clean[i].start && clean[j].end === clean[i].end) j++;
    const start = clean[i].start;
    const next = j < clean.length ? clean[j].start : start;
    const end = clean[i].end > start ? clean[i].end : Math.max(start, next);
    if (j - i === 1) {
      out.push({ word: clean[i].word, start, end });
    } else {
      const per = (end - start) / (j - i);
      for (let k = i; k < j; k++) out.push({ word: clean[k].word, start: start + (k - i) * per, end: start + (k - i + 1) * per });
    }
    i = j;
  }
  return out;
}

/** Caption blocks: a block ends at a sentence's end or after MAX_BLOCK_WORDS words. */
export function captionBlocks(words: TranscriptWord[]): CaptionBlock[] {
  const blocks: CaptionBlock[] = [];
  let current: CaptionBlock | null = null;
  let count = 0;
  for (const w of words) {
    const word = w.word.trim();
    if (!word) continue;
    if (!current) current = { text: word, startTime: w.start, endTime: w.end };
    else { current.text += " " + word; current.endTime = w.end; }
    count++;
    if (count >= MAX_BLOCK_WORDS || /[.!?]["')\]]?$/.test(word)) {
      blocks.push(current);
      current = null;
      count = 0;
    }
  }
  if (current) blocks.push(current);
  return blocks;
}

/** The block on screen at `time` (seconds into the audio), or -1. A block is held until the next starts or HOLD_SECONDS pass. */
export function activeBlockIndex(blocks: CaptionBlock[], time: number): number {
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const next = blocks[i + 1];
    const until = next ? Math.max(block.endTime, Math.min(next.startTime, block.endTime + HOLD_SECONDS)) : block.endTime + HOLD_SECONDS;
    if (time >= block.startTime && time < until) return i;
  }
  return -1;
}
